// Verified against backend substitution calls in:
//   process-bookings/alerts/process_customer_trip_notifications.py
//   process-bookings/alerts/process_driver_trip_notifications.py
//   process-bookings/alerts/process_post_trip_notifications.py
//   process-bookings/alerts/process_customer_post_trip_notifications.py
//   process-bookings/alerts/process_relay_notifications.py
//   process-bookings/alerts/process_late_alerts.py
//   process-bookings/alerts/process_vehicle_late_alerts.py
//   process-bookings/alerts/process_missing_dvir_alerts.py
//   process-bookings/alerts/process_driver_surveys.py
//   process-bookings/alerts/process_passenger_trip_notifications.py
//   process-bookings/alerts/process_unplanned_vehicle_activity_alert.py
//   fleet-int-api/alerts/process_gateway_disconnect_alert.py
//   fleet-int-api/alerts/process_driver_hos_alerts.py
//   fleet-int-api/alerts/process_vehicle_inactivity_alert.py
//   fleet-int-api/alerts/process_vehicle_eld_alert.py
//   fleet-int-api/users/group_message_sender.py
//   fleet-int-api/fasttrak/fasttrak_late_alerts.py
//   tbn-integration/alerts/tbn_customer_trip_notifications.py
//   tbn-integration/alerts/tbn_driver_trip_notifications.py
//   tbn-integration/alerts/tbn_late_alerts.py
//   tbn-integration/alerts/tbn_vehicle_late_alerts.py
//   tbn-integration/alerts/tbn_customer_post_trip_notifications.py
//   tbn-integration/alerts/tbn_unplanned_vehicle_activity_alert.py
//
// Re-audited FI-1991: every field below was checked against a literal
// replace_template_value(..., "[token]", ...) call in the relevant source
// file(s) (case-insensitive, matching the backend's fill logic).

// Customer trip SMS / customer alert event templates
// Source: process_customer_trip_notifications.py
export const CUSTOMER_ALERT_FIELDS = [
  { name: 'vehicleID', description: 'Vehicle registration / ID' },
  { name: 'vehicleType', description: 'Vehicle type (e.g. Coach, Minibus)' },
  { name: 'vehicleSeats', description: 'Number of seats on the assigned vehicle' },
  { name: 'charterID', description: 'Charter / booking reference number' },
  { name: 'movementID', description: 'Movement ID within the booking' },
  { name: 'description', description: 'Movement description / trip notes' },
  { name: 'bookingType', description: 'Type of booking (e.g. Charter, Transfer)' },
  { name: 'clientID', description: 'Client / account ID' },
  { name: 'clientReference1', description: 'Client reference field 1' },
  { name: 'clientReference2', description: 'Client reference field 2' },
  { name: 'groupName', description: 'Name of the passenger group' },
  { name: 'startBase', description: 'Start base code / name' },
  { name: 'startBaseDesc', description: 'Start base description' },
  { name: 'startDate', description: 'Date the booking starts' },
  { name: 'pickupDate', description: 'Date of passenger pickup' },
  { name: 'startTime', description: 'Start time (12-hour)' },
  { name: 'spotTime', description: 'Driver spot / report time (12-hour)' },
  { name: 'pickupTime', description: 'Passenger pickup time (12-hour)' },
  { name: 'arrivalTime', description: 'Arrival time (12-hour)' },
  { name: 'leaveTime', description: 'Leave time (12-hour)' },
  { name: 'backTime', description: 'Return / back time (12-hour)' },
  { name: 'finishTime', description: 'Scheduled finish time (12-hour)' },
  { name: 'startTime24', description: 'Start time (24-hour)' },
  { name: 'spotTime24', description: 'Driver spot time (24-hour)' },
  { name: 'pickupTime24', description: 'Pickup time (24-hour)' },
  { name: 'backTime24', description: 'Back time (24-hour)' },
  { name: 'finishTime24', description: 'Finish time (24-hour)' },
  { name: 'pickupLocation', description: 'Passenger pickup location / address' },
  { name: 'destinationLocation', description: 'Destination / drop-off address' },
  { name: 'pickupInstructions', description: 'Special instructions for pickup' },
  { name: 'destinationInstructions', description: 'Special instructions for the destination' },
  { name: 'leaderName', description: 'Name of the group leader / point of contact' },
  { name: 'leaderNumber', description: 'Phone number of the group leader' },
  { name: 'driverName', description: 'Full name of the assigned driver' },
  { name: 'driverNumber', description: 'Phone number of the assigned driver' },
  { name: 'driverFirstName', description: 'First name of the assigned driver' },
  { name: 'driverLastName', description: 'Last name of the assigned driver' },
  { name: 'bookingID', description: 'Booking reference ID (alias of charterID)' },
  { name: 'assignmentID', description: 'Assignment ID within the booking (TBN)', only: 'TBN' },
  { name: 'driverStartTime', description: 'Time the driver started (12-hour, TBN)', only: 'TBN' },
  { name: 'driverStartTime24', description: 'Time the driver started (24-hour, TBN)', only: 'TBN' },
  { name: 'bookingMapLink', description: 'Link to a map of the full booking route' },
  { name: 'routeMapLink', description: 'Link to the route map (TBN)', only: 'TBN' },
  { name: 'pickupMapLink', description: 'Link to a map of the pickup location' },
  { name: 'dropoffLocation', description: 'Drop-off / destination address (TBN)', only: 'TBN' },
  { name: 'bookingPrimaryContactName', description: 'Primary contact full name from the TBN booking', only: 'TBN' },
  { name: 'bookingPrimaryContactOrganization', description: 'Primary contact organisation name from the TBN booking', only: 'TBN' },
  { name: 'driverInfo', description: 'Formatted block with driver name and contact details' },
  { name: 'vehicleAndDriverInfo', description: 'Formatted block with vehicle and driver details' },
  { name: 'vehicleTypeAndDriverInfo', description: 'Formatted block with vehicle type, capacity, and driver' },
  { name: 'vehicleTypeAndDriverInfoHTML', description: 'HTML-formatted block with vehicle type and driver (for email templates)' },
  { name: 'vehicleTypeAndDriverInfoImages', description: 'Block with vehicle/driver details including images' },
  { name: 'vehicleTypeAndDriverWithImage', description: 'Block with vehicle/driver details and driver photo' },
]

// Customer pre-trip HTML email template
//
// IMPORTANT: unlike every other category in this file, CM and TBN do NOT
// share a fill function here, and their supported chip vocabularies barely
// overlap (3 words). Do not spread CUSTOMER_ALERT_FIELDS into this list —
// that list is for the SMS/plain-text body, filled by a different function
// with a different vocabulary, and most of it silently no-ops in HTML email.
//
// CM source: process_customer_trip_notifications.py::fill_html_template
//   (PascalCase tokens; matched case-insensitively by replace_template_value)
// TBN source: tbn_customer_trip_notifications.py::fill_template
//   (same function used for TBN's SMS body and HTML email body)
export const CUSTOMER_PRE_TRIP_EMAIL_FIELDS = [
  // Supported by both CM and TBN
  { name: 'pickupDate', description: 'Date of passenger pickup' },
  { name: 'pickupLocation', description: 'Passenger pickup location / address' },
  { name: 'destinationLocation', description: 'Destination / drop-off address' },

  // CM only (fill_html_template)
  { name: 'charterID', description: 'Charter / booking reference number', only: 'CM' },
  { name: 'movementID', description: 'Movement ID within the booking', only: 'CM' },
  { name: 'group', description: 'Passenger group name', only: 'CM' },
  { name: 'movementDescription', description: 'Movement description / trip notes', only: 'CM' },
  { name: 'pointOfContact', description: 'Group leader / point of contact name', only: 'CM' },
  { name: 'positionDateTime', description: 'Date and time of the vehicle position', only: 'CM' },
  { name: 'arrivalTime', description: 'Arrival time', only: 'CM' },
  { name: 'leaveTime', description: 'Leave time', only: 'CM' },
  { name: 'backTime', description: 'Return / back time', only: 'CM' },
  { name: 'pickupDateTime', description: 'Full date and time of pickup', only: 'CM' },
  { name: 'pickupInstructions', description: 'Special instructions for pickup', only: 'CM' },
  { name: 'destinationInstructions', description: 'Special instructions for the destination', only: 'CM' },
  { name: 'returnDateTime', description: 'Full date and time of the return journey', only: 'CM' },
  { name: 'bookedBySignatoryName', description: 'Name of the sales rep / signatory who made the booking', only: 'CM' },
  { name: 'company', description: 'Company / tenant name', only: 'CM' },
  { name: 'startBase', description: 'Start base code / name', only: 'CM' },
  { name: 'startBaseDesc', description: 'Start base description', only: 'CM' },
  { name: 'bookingTrackingURL', description: 'URL for passengers to track the vehicle live', only: 'CM' },
  { name: 'driverInfo', description: 'Formatted block with driver name and contact details', only: 'CM' },
  { name: 'vehicleAndDriverInfo', description: 'Formatted block with vehicle and driver details', only: 'CM' },
  { name: 'vehicleTypeAndDriverInfo', description: 'Formatted block with vehicle type, capacity, and driver', only: 'CM' },
  { name: 'vehicleTypeAndDriverInfoImages', description: 'Block with vehicle/driver details including images', only: 'CM' },
  { name: 'vehicleTypeAndDriverWithImage', description: 'Block with vehicle/driver details and driver photo', only: 'CM' },

  // TBN only (fill_template, same function used for TBN's SMS and HTML)
  { name: 'vehicleID', description: 'Vehicle registration / ID', only: 'TBN' },
  { name: 'driverName', description: 'Full name of the assigned driver', only: 'TBN' },
  { name: 'driverFirstName', description: 'First name of the assigned driver', only: 'TBN' },
  { name: 'driverLastName', description: 'Last name of the assigned driver', only: 'TBN' },
  { name: 'driverNumber', description: 'Phone number of the assigned driver', only: 'TBN' },
  { name: 'bookingID', description: 'Booking reference ID', only: 'TBN' },
  { name: 'assignmentID', description: 'Assignment ID within the booking', only: 'TBN' },
  { name: 'driverStartTime', description: 'Time the driver started (12-hour)', only: 'TBN' },
  { name: 'driverStartTime24', description: 'Time the driver started (24-hour)', only: 'TBN' },
  { name: 'startDate', description: 'Date the booking starts', only: 'TBN' },
  { name: 'startTime', description: 'Start time (12-hour)', only: 'TBN' },
  { name: 'startTime24', description: 'Start time (24-hour)', only: 'TBN' },
  { name: 'spotTime', description: 'Driver spot / report time (12-hour)', only: 'TBN' },
  { name: 'spotTime24', description: 'Driver spot time (24-hour)', only: 'TBN' },
  { name: 'pickupTime', description: 'Passenger pickup time (12-hour)', only: 'TBN' },
  { name: 'pickupTime24', description: 'Pickup time (24-hour)', only: 'TBN' },
  { name: 'dropoffLocation', description: 'Drop-off / destination address', only: 'TBN' },
  { name: 'bookingMapLink', description: 'Link to a map of the full booking route', only: 'TBN' },
  { name: 'routeMapLink', description: 'Link to the route map', only: 'TBN' },
  { name: 'bookingPrimaryContactName', description: 'Primary contact full name from the TBN booking', only: 'TBN' },
  { name: 'bookingPrimaryContactOrganization', description: 'Primary contact organisation name from the TBN booking', only: 'TBN' },
]

// Driver pre-trip SMS template
// Source: process_driver_trip_notifications.py
export const DRIVER_TRIP_FIELDS = [
  { name: 'vehicleID', description: 'Vehicle registration / ID' },
  { name: 'charterID', description: 'Charter / booking reference number' },
  { name: 'movementID', description: 'Movement ID within the booking' },
  { name: 'bookingType', description: 'Type of booking (e.g. Charter, Transfer)' },
  { name: 'clientID', description: 'Client / account ID' },
  { name: 'groupName', description: 'Name of the passenger group' },
  { name: 'description', description: 'Movement description / trip notes' },
  { name: 'destination', description: 'Trip destination' },
  { name: 'startBase', description: 'Start base code / name' },
  { name: 'startBaseDesc', description: 'Start base description' },
  { name: 'startDate', description: 'Date the booking starts' },
  { name: 'pickupDate', description: 'Date of passenger pickup' },
  { name: 'startTime', description: 'Start time (12-hour)' },
  { name: 'spotTime', description: 'Driver spot / report time (12-hour)' },
  { name: 'pickupTime', description: 'Passenger pickup time (12-hour)' },
  { name: 'backTime', description: 'Return / back time (12-hour)' },
  { name: 'finishTime', description: 'Scheduled finish time (12-hour)' },
  { name: 'startTime24', description: 'Start time (24-hour)' },
  { name: 'spotTime24', description: 'Driver spot time (24-hour)' },
  { name: 'pickupTime24', description: 'Pickup time (24-hour)' },
  { name: 'backTime24', description: 'Back time (24-hour)' },
  { name: 'finishTime24', description: 'Finish time (24-hour)' },
  { name: 'pickupLocation', description: 'Passenger pickup address' },
  { name: 'pickupInstructions', description: 'Special instructions for pickup' },
  { name: 'driverInstructions', description: 'Special instructions for the driver' },
  { name: 'leaderName', description: 'Group leader name(s)' },
  { name: 'leaderNumber', description: 'Group leader phone number(s)' },
  { name: 'driverName', description: 'Driver name' },
  { name: 'driverNumber', description: 'Driver phone number' },
  { name: 'driverNameAndNumber', description: 'Driver name and phone combined' },
  { name: 'vehicleMapLink', description: 'Link to vehicle location on a map' },
  { name: 'bookingMapLink', description: 'Link to full booking route map' },
  { name: 'pickupMapLink', description: 'Link to pickup location map' },
  { name: 'confirmLink', description: 'Link for the driver to confirm the booking (line removed if unused)' },
  { name: 'assignmentID', description: 'Assignment ID within the booking (TBN)', only: 'TBN' },
  { name: 'bookingID', description: 'Booking reference ID (TBN)', only: 'TBN' },
  { name: 'driverStartTime', description: 'Time the driver started (12-hour, TBN)', only: 'TBN' },
  { name: 'driverStartTime24', description: 'Time the driver started (24-hour, TBN)', only: 'TBN' },
  { name: 'driverEndTime', description: 'Time the driver finished (12-hour, TBN)', only: 'TBN' },
  { name: 'driverEndTime24', description: 'Time the driver finished (24-hour, TBN)', only: 'TBN' },
  { name: 'dropoffLocation', description: 'Drop-off / destination address (TBN)', only: 'TBN' },
]

// Driver post-trip SMS template
// Source: process_post_trip_notifications.py
export const DRIVER_POST_TRIP_FIELDS = [
  { name: 'vehicleID', description: 'Vehicle registration / ID' },
  { name: 'charterID', description: 'Charter / booking reference number' },
  { name: 'bookingID', description: 'Booking reference ID (alias of charterID)' },
  { name: 'movementID', description: 'Movement ID' },
  { name: 'bookingType', description: 'Type of booking' },
  { name: 'clientID', description: 'Client / account ID' },
  { name: 'driverName', description: 'Driver name' },
  { name: 'driverNumber', description: 'Driver phone number' },
  { name: 'driverPhone', description: 'Driver phone number (alternate token)' },
  { name: 'startDate', description: 'Date the booking started' },
  { name: 'startTime', description: 'Start time (12-hour)' },
  { name: 'spotTime', description: 'Driver spot time (12-hour)' },
  { name: 'pickupTime', description: 'Pickup time (12-hour)' },
  { name: 'finishTime', description: 'Finish time (12-hour)' },
  { name: 'backTime', description: 'Back time (12-hour)' },
  { name: 'startTime24', description: 'Start time (24-hour)' },
  { name: 'spotTime24', description: 'Driver spot time (24-hour)' },
  { name: 'pickupTime24', description: 'Pickup time (24-hour)' },
  { name: 'finishTime24', description: 'Finish time (24-hour)' },
  { name: 'backTime24', description: 'Back time (24-hour)' },
  { name: 'startLocation', description: 'Trip start base / location' },
  { name: 'pickupLocation', description: 'Passenger pickup address' },
  { name: 'finishLocation', description: 'Trip finish base / location' },
  { name: 'completedLocation', description: 'Where the trip completed (destination if finished, otherwise pickup)' },
  { name: 'trackVehicleLink', description: 'Link to track the vehicle' },
  { name: 'vehicleMapLink', description: 'Link to vehicle location on a map' },
  { name: 'bookingMapLink', description: 'Link to full booking route map' },
  { name: 'pickupMapLink', description: 'Link to pickup location map' },
]

// Customer post-trip SMS / email templates
// Source: process_customer_post_trip_notifications.py
export const CUSTOMER_POST_TRIP_FIELDS = [
  { name: 'vehicleID', description: 'Vehicle registration / ID' },
  { name: 'vehicleType', description: 'Vehicle type (e.g. Coach, Minibus)' },
  { name: 'charterID', description: 'Charter / booking reference number' },
  { name: 'movementID', description: 'Movement ID' },
  { name: 'bookingType', description: 'Type of booking' },
  { name: 'clientID', description: 'Client / account ID' },
  { name: 'clientReference1', description: 'Client reference field 1' },
  { name: 'clientReference2', description: 'Client reference field 2' },
  { name: 'groupName', description: 'Name of the passenger group' },
  { name: 'description', description: 'Movement description' },
  { name: 'startBase', description: 'Start base code / name' },
  { name: 'startDate', description: 'Date the booking started' },
  { name: 'pickupDate', description: 'Date of passenger pickup' },
  { name: 'finishDate', description: 'Date the booking finished' },
  { name: 'startTime', description: 'Start time (12-hour)' },
  { name: 'spotTime', description: 'Driver spot time (12-hour)' },
  { name: 'pickupTime', description: 'Pickup time (12-hour)' },
  { name: 'backTime', description: 'Back time (12-hour)' },
  { name: 'finishTime', description: 'Finish time (12-hour)' },
  { name: 'startTime24', description: 'Start time (24-hour)' },
  { name: 'spotTime24', description: 'Driver spot time (24-hour)' },
  { name: 'pickupTime24', description: 'Pickup time (24-hour)' },
  { name: 'backTime24', description: 'Back time (24-hour)' },
  { name: 'finishTime24', description: 'Finish time (24-hour)' },
  { name: 'leaderName', description: 'Group leader name' },
  { name: 'leaderNumber', description: 'Group leader phone number' },
  { name: 'driverName', description: 'Driver name' },
  { name: 'driverNumber', description: 'Driver phone number' },
  { name: 'driverNameInUrl', description: 'URL-encoded driver name (for survey links)' },
  { name: 'cmDriverID', description: 'Driver ID (for survey / URL construction)' },
  { name: 'driverInfo', description: 'Formatted block with driver name and contact details' },
  { name: 'vehicleAndDriverInfo', description: 'Formatted block with vehicle and driver details' },
  { name: 'vehicleTypeAndDriverInfo', description: 'Formatted block with vehicle type, capacity, and driver' },
]

// Relay notifications SMS template
// Source: process_relay_notifications.py
export const RELAY_NOTIFICATION_FIELDS = [
  { name: 'vehicleID', description: 'Vehicle registration / ID' },
  { name: 'charterID', description: 'Charter / booking reference number' },
  { name: 'movementID', description: 'Movement ID' },
  { name: 'startDate', description: 'Date the relay starts' },
  { name: 'startTime', description: 'Start time (12-hour)' },
  { name: 'driverName', description: 'Driver name' },
  { name: 'driverNumber', description: 'Driver phone number' },
  { name: 'driveDistance', description: 'Distance of the relay drive' },
  { name: 'driveTime', description: 'Estimated relay drive time' },
  { name: 'finishPoint', description: 'Final destination address of the relay' },
  { name: 'driverInfo', description: 'Formatted block with driver name and contact' },
  { name: 'vehicleAndDriverInfo', description: 'Formatted block with vehicle and driver details' },
  { name: 'vehicleAndDriverInfoMapLink', description: 'Vehicle/driver details block with map link' },
  { name: 'vehicleMapLink', description: 'Link to vehicle location on a map' },
  { name: 'bookingMapLink', description: 'Link to full booking route map' },
  { name: 'pickupMapLink', description: 'Link to pickup location map' },
]

// Driver surveys SMS / email templates
// Source: process_driver_surveys.py
export const DRIVER_SURVEY_FIELDS = [
  { name: 'contactName', description: 'Name of the primary contact' },
  { name: 'driverName', description: 'Full name of the driver' },
  { name: 'companyName', description: 'Company / tenant name' },
  { name: 'companyID', description: 'Company / tenant ID' },
  { name: 'clientID', description: 'Client / account ID' },
  { name: 'privatehireID', description: 'Private hire reference ID' },
  { name: 'startDate', description: 'Date the booking started (YYYY-MM-DD)' },
]

// Passenger trip SMS templates
// Source: process_passenger_trip_notifications.py
export const PASSENGER_TRIP_FIELDS = [
  { name: 'tourType', description: 'Type of tour or trip' },
  { name: 'mapLink', description: 'Link to a map' },
  { name: 'bookingPickupTime', description: 'Scheduled pickup time for the booking' },
  { name: 'bookingPickup', description: 'Pickup location for the booking' },
  { name: 'Time STR', description: 'Passenger-specific time string' },
  { name: 'pickupPoint', description: 'Specific pickup point name' },
]

// Late alert SMS template
// Source: process_late_alerts.py
export const LATE_ALERT_FIELDS = [
  { name: 'vehicleID', description: 'Vehicle registration / ID' },
  { name: 'charterID', description: 'Charter / booking reference number' },
  { name: 'bookingID', description: 'Booking reference ID (TBN, alias of charterID)' },
  { name: 'movementID', description: 'Movement ID' },
  { name: 'description', description: 'Movement description' },
  { name: 'clientID', description: 'Client / account ID' },
  { name: 'driverName', description: 'Driver name' },
  { name: 'driverNumber', description: 'Driver phone number' },
  { name: 'startDate', description: 'Date the booking started' },
  { name: 'startTime', description: 'Start time (12-hour)' },
  { name: 'spotTime', description: 'Driver spot time (12-hour)' },
  { name: 'pickupTime', description: 'Passenger pickup time (12-hour)' },
  { name: 'startTime24', description: 'Start time (24-hour)' },
  { name: 'spotTime24', description: 'Driver spot time (24-hour)' },
  { name: 'pickupTime24', description: 'Pickup time (24-hour)' },
  { name: 'pickupLocation', description: 'Passenger pickup address' },
  { name: 'assignmentID', description: 'Assignment ID within the booking (TBN)', only: 'TBN' },
  { name: 'driverStartTime', description: 'Time the driver started (12-hour, TBN)', only: 'TBN' },
  { name: 'driverStartTime24', description: 'Time the driver started (24-hour, TBN)', only: 'TBN' },
]

// Vehicle late alert SMS template
// Source: process_vehicle_late_alerts.py / fleet-int-api/fasttrak/fasttrak_late_alerts.py
export const VEHICLE_LATE_ALERT_FIELDS = [
  ...LATE_ALERT_FIELDS,
  { name: 'minutesThreshold', description: 'Minutes-late threshold that triggered this alert' },
  { name: 'distanceThreshold', description: 'Distance threshold that triggered this alert' },
  { name: 'reservationID', description: 'Fasttrak reservation ID' },
  { name: 'estimatedPickupTime', description: 'Estimated pickup time calculated by Fasttrak' },
]

// Missing DVIR alert SMS templates (pre-trip and post-trip)
// Source: process_missing_dvir_alerts.py
export const MISSING_DVIR_FIELDS = [
  { name: 'vehicleID', description: 'Vehicle registration / ID' },
  { name: 'bookingID', description: 'Booking reference ID' },
  { name: 'movementID', description: 'Movement ID' },
  { name: 'driverName', description: 'Driver name' },
  { name: 'driverNumber', description: 'Driver phone number' },
  { name: 'driverStartTime', description: 'Time the driver started the trip (from movement record)' },
  { name: 'startTime', description: 'Scheduled start time' },
  { name: 'finishTime', description: 'Scheduled finish time' },
  { name: 'startLocation', description: 'Trip start base / location' },
  { name: 'finishLocation', description: 'Trip finish base / location' },
]

// Gateway disconnect alert SMS template
// Source: fleet-int-api/alerts/process_gateway_disconnect_alert.py
export const GATEWAY_DISCONNECT_FIELDS = [
  { name: 'vehicleID', description: 'Vehicle registration / ID' },
  { name: 'gatewaySerial', description: 'Serial number of the disconnected gateway device' },
  { name: 'disconnectReason', description: 'Reason the gateway disconnected' },
  { name: 'driverFirstName', description: 'Driver first name' },
  { name: 'driverLastName', description: 'Driver last name' },
]

// Vehicle inactivity alert SMS template
// Source: fleet-int-api/alerts/process_vehicle_inactivity_alert.py
export const VEHICLE_INACTIVITY_FIELDS = [
  { name: 'vehicleID', description: 'Vehicle registration / ID' },
  { name: 'driverFirstName', description: 'Driver first name' },
]

// Driver HOS (Hours of Service) alert SMS template
// Source: fleet-int-api/alerts/process_driver_hos_alerts.py
export const DRIVER_HOS_FIELDS = [
  { name: 'driverName', description: 'Full name of the driver' },
  { name: 'driverFirstName', description: 'Driver first name' },
  { name: 'driverLastName', description: 'Driver last name' },
  { name: 'daysAgo', description: 'Number of days ago the HOS violation occurred' },
  { name: 'eventType', description: 'Type of HOS event (e.g. On Duty, Driving)' },
  { name: 'onDutyHours', description: 'Total on-duty hours logged' },
  { name: 'drivingHours', description: 'Total driving hours logged' },
  { name: 'onDutyOrDrivingHours', description: 'On-duty or driving hours (whichever applies to the violation)' },
  { name: 'hourLimit', description: 'Hour limit that was exceeded' },
  { name: 'dayLimit', description: 'Day limit that was exceeded' },
]

// Unplanned vehicle activity alert SMS template
// Source: process_unplanned_vehicle_activity_alert.py
export const UNPLANNED_VEHICLE_ACTIVITY_FIELDS = [
  { name: 'vehicleID', description: 'Vehicle registration / ID' },
  { name: 'vehicleMapLink', description: 'Link to vehicle location on a map (TBN)', only: 'TBN' },
]

// ELD alert — shared base tokens
const ELD_BASE_FIELDS = [
  { name: 'vehicleID', description: 'Vehicle registration / ID' },
  { name: 'driverFirstName', description: 'Driver first name' },
  { name: 'driverLastName', description: 'Driver last name' },
]

// ELD idle alert SMS template (eld_alert_idle_alert_sms_template)
// Source: fleet-int-api/alerts/process_vehicle_eld_alert.py
export const ELD_IDLE_FIELDS = [
  ...ELD_BASE_FIELDS,
  { name: 'idleMinutes', description: 'Number of minutes the vehicle has been idling' },
]

// ELD idle geofence alert SMS template (eld_alert_idle_alert_sms_geofence_template)
// Source: fleet-int-api/alerts/process_vehicle_eld_alert.py
export const ELD_GEOFENCE_FIELDS = [
  ...ELD_BASE_FIELDS,
]

// ELD fault alert SMS template (eld_alert_fault_alert_sms_template)
// Source: fleet-int-api/alerts/process_vehicle_eld_alert.py
export const ELD_FAULT_FIELDS = [
  ...ELD_BASE_FIELDS,
  { name: 'faultCode', description: 'Fault code description(s) from the ELD event' },
  { name: 'faultInstructions', description: 'Instructions associated with the fault code' },
]

// Late alert voice call message
// Source: process_late_alerts.py / process_vehicle_late_alerts.py / tbn_late_alerts.py / tbn_vehicle_late_alerts.py (voice_msg replace calls only)
export const LATE_ALERT_VOICE_FIELDS = [
  { name: 'vehicleID', description: 'Vehicle registration / ID' },
  { name: 'charterID', description: 'Charter / booking reference number' },
  { name: 'movementID', description: 'Movement ID' },
  { name: 'driverName', description: 'Driver name' },
  { name: 'driverNumber', description: 'Driver phone number' },
  { name: 'spotTime', description: 'Driver spot / report time (TBN vehicle late alerts only)' },
  { name: 'pickupLocation', description: 'Passenger pickup address (TBN vehicle late alerts only)' },
]

// Customer trip notifications email subject line
// Source: process-bookings ProcessManager.fill_email_subject (CM) / tbn_customer_trip_notifications.py
// (email_subject replace calls: charterID, startDate, pickupDate only)
export const CUSTOMER_ALERT_EMAIL_SUBJECT_FIELDS = [
  { name: 'charterID', description: 'Charter / booking reference number' },
  { name: 'movementID', description: 'Movement ID within the booking', only: 'CM' },
  { name: 'description', description: 'Movement description / trip notes', only: 'CM' },
  { name: 'pickupDate', description: 'Pickup date (MM/DD)' },
  { name: 'startDate', description: 'Trip start date (MM/DD)' },
]

// Customer post-trip notifications email subject line
// Source: process-bookings ProcessManager.fill_email_subject (CM) / tbn_customer_post_trip_notifications.py
// (email_subject replace calls: charterID only)
export const CUSTOMER_POST_TRIP_EMAIL_SUBJECT_FIELDS = [
  { name: 'charterID', description: 'Charter / booking reference number' },
  { name: 'movementID', description: 'Movement ID within the booking', only: 'CM' },
  { name: 'description', description: 'Movement description / trip notes', only: 'CM' },
  { name: 'pickupDate', description: 'Pickup date (MM/DD)', only: 'CM' },
  { name: 'startDate', description: 'Trip start date (MM/DD)', only: 'CM' },
]

// Group message sender SMS / email
// Source: fleet-int-api/users/group_message_sender.py
export const GROUP_MESSAGE_FIELDS = [
  { name: 'firstName', description: 'Recipient first name' },
  { name: 'lastName', description: 'Recipient last name' },
  { name: 'fullName', description: 'Recipient full name' },
  { name: 'senderName', description: 'Full name of the person sending the message' },
  { name: 'senderEmail', description: 'Email address of the person sending the message' },
]

// Full category list used by the Template Fields help tab.
// Each entry maps a human-readable label to its verified field set.
export const PFS_CATEGORIES = [
  { id: 'customerAlert',      label: 'Customer Alert SMS / Email',          fields: CUSTOMER_ALERT_FIELDS },
  { id: 'customerPreTripEmail', label: 'Customer Pre-Trip HTML Email',       fields: CUSTOMER_PRE_TRIP_EMAIL_FIELDS },
  { id: 'driverTrip',         label: 'Driver Trip SMS',                     fields: DRIVER_TRIP_FIELDS },
  { id: 'driverPostTrip',     label: 'Driver Post-Trip SMS',                fields: DRIVER_POST_TRIP_FIELDS },
  { id: 'customerPostTrip',   label: 'Customer Post-Trip',                  fields: CUSTOMER_POST_TRIP_FIELDS },
  { id: 'relay',              label: 'Relay Notification SMS',              fields: RELAY_NOTIFICATION_FIELDS },
  { id: 'surveys',            label: 'Driver Survey SMS / Email',           fields: DRIVER_SURVEY_FIELDS },
  { id: 'passenger',          label: 'Passenger Trip SMS',                  fields: PASSENGER_TRIP_FIELDS },
  { id: 'lateAlert',          label: 'Late Alert SMS',                      fields: LATE_ALERT_FIELDS },
  { id: 'lateAlertVoice',     label: 'Late Alert Voice Message',            fields: LATE_ALERT_VOICE_FIELDS },
  { id: 'customerAlertEmailSubject', label: 'Customer Alert Email Subject', fields: CUSTOMER_ALERT_EMAIL_SUBJECT_FIELDS },
  { id: 'customerPostTripEmailSubject', label: 'Customer Post-Trip Email Subject', fields: CUSTOMER_POST_TRIP_EMAIL_SUBJECT_FIELDS },
  { id: 'vehicleLateAlert',   label: 'Vehicle Late Alert SMS',              fields: VEHICLE_LATE_ALERT_FIELDS },
  { id: 'missingDvir',        label: 'Missing DVIR SMS',                    fields: MISSING_DVIR_FIELDS },
  { id: 'eldIdle',            label: 'ELD Idle Alert SMS',                  fields: ELD_IDLE_FIELDS },
  { id: 'eldGeofence',        label: 'ELD Idle Geofence Alert SMS',         fields: ELD_GEOFENCE_FIELDS },
  { id: 'eldFault',           label: 'ELD Fault Alert SMS',                 fields: ELD_FAULT_FIELDS },
  { id: 'gatewayDisconnect',  label: 'Gateway Disconnect SMS',              fields: GATEWAY_DISCONNECT_FIELDS },
  { id: 'vehicleInactivity',  label: 'Vehicle Inactivity SMS',              fields: VEHICLE_INACTIVITY_FIELDS },
  { id: 'driverHos',          label: 'Driver HOS SMS',                      fields: DRIVER_HOS_FIELDS },
  { id: 'unplannedActivity',  label: 'Unplanned Vehicle Activity SMS',      fields: UNPLANNED_VEHICLE_ACTIVITY_FIELDS },
  { id: 'groupMessage',       label: 'Group Message',                       fields: GROUP_MESSAGE_FIELDS },
]
